import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/context/CartContext";

const links = [
  { to: "/legacy", label: "Home", end: true },
  { to: "/legacy/collections", label: "Collections" },
  { to: "/legacy/designer", label: "Custom Studio" },
  { to: "/legacy/gallery", label: "Gallery" },
  { to: "/legacy/about", label: "Atelier" },
  { to: "/legacy/faqs", label: "FAQs" },
  { to: "/legacy/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { count, setIsOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#D8D4CC]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20 md:h-24">
        <Link to="/legacy" data-testid="nav-logo-link" className="flex items-center gap-3">
          <Logo className="w-9 h-9 text-[#1B3B36]" />
          <div className="leading-none">
            <div className="font-serif text-xl md:text-2xl tracking-tight text-[#1B3B36]">
              Iryaa Homes
            </div>
            <div className="overline text-[10px] mt-1">every thread, a story</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-medium transition-colors ${
                  isActive
                    ? "text-[#1B3B36]"
                    : "text-[#4A4A4A] hover:text-[#1B3B36]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={() => setIsOpen(true)}
            data-testid="nav-cart-button"
            className="relative w-10 h-10 flex items-center justify-center text-[#1B3B36] hover:bg-[#EFECE5] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 bg-[#1B3B36] text-[#F9F8F6] text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <Link
            to="/legacy/designer"
            data-testid="nav-cta-design"
            className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-6 py-3 text-[12px] tracking-[0.18em] uppercase hover:bg-[#132A26] transition-colors"
          >
            Design Yours
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <button
            onClick={() => setIsOpen(true)}
            data-testid="mobile-cart-button"
            className="relative w-10 h-10 flex items-center justify-center text-[#1B3B36]"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#1B3B36] text-[#F9F8F6] text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="text-[#1B3B36]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden bg-[#F9F8F6] border-b border-[#D8D4CC] transition-[max-height] duration-500 ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `font-serif text-2xl ${
                  isActive ? "text-[#1B3B36]" : "text-[#4A4A4A]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/legacy/designer"
            data-testid="mobile-cta-design"
            className="mt-4 inline-flex justify-center bg-[#1B3B36] text-[#F9F8F6] px-6 py-3 text-[12px] tracking-[0.18em] uppercase"
          >
            Design Yours
          </Link>
        </div>
      </div>
    </header>
  );
}
